// AdminExportToExcel.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronDown,
  ChevronUp,
  HelpCircle,
  RefreshCw,
  Download,
  Search,
  CalendarDays,
  ChevronLeft,
} from "lucide-react";

/**
 * Export To Excel (ASPX -> React)
 *
 * APIs (Node):
 * GET  /api/export-to-excel/modules
 * POST /api/export-to-excel/tables        body: { moduleIds: number[] }
 * POST /api/export-to-excel/export        body: { tableId, format, fromDate, toDate }  -> file stream
 *
 * Notes:
 * - Dates are dd/MM/yyyy like ASPX
 * - Header x-finyear must be "2025-2026" (YYYY-YYYY)
 */
const AdminExportToExcel = () => {
  const BASE_URL = "https://nissi-india.com";
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = useMemo(
    () => `bg-${baseColor}-${baseShade}`,
    [baseColor, baseShade]
  );
  const hoverClass = useMemo(() => `hover:bg-${baseColor}-700`, [baseColor]);

  const [showForm, setShowForm] = useState(true);

  // ✅ finYear string (YYYY-YYYY) used in header, NOT finYearId
  const finYear = (localStorage.getItem("finYear") || "").trim();
  const headers = useMemo(
    () => (finYear ? { "x-finyear": finYear } : {}),
    [finYear]
  );

  // Dates (dd/MM/yyyy)
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return toDDMMYYYY(d);
  });
  const [toDate, setToDate] = useState(() => toDDMMYYYY(new Date()));

  // Modules grid
  const [modules, setModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);
  const [moduleSearch, setModuleSearch] = useState("");
  const [selectedModuleIds, setSelectedModuleIds] = useState(new Set());

  // Tables grid (based on selected modules)
  const [tables, setTables] = useState([]);
  const [loadingTables, setLoadingTables] = useState(false);
  const [tableSearch, setTableSearch] = useState("");

  // Per-row selections (PDF/Excel + Select)
  const [rowState, setRowState] = useState({}); // { [tableId]: { selected, pdf, excel } }

  const [exporting, setExporting] = useState(false);

  const handleBack = () => {
    if (exporting) return;
    navigate("/admin/adminhome");
  };

  // ---------- Fetch Modules ----------
  const fetchModules = async () => {
    try {
      if (!finYear) {
        toast.error("finYear missing! Please login again (finYear like 2025-2026).");
        return;
      }

      setLoadingModules(true);
      const res = await axios.get(`${BASE_URL}/api/export-to-excel/modules`, {
        headers,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load modules");
        return;
      }

      setModules(res.data.list || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load modules");
    } finally {
      setLoadingModules(false);
    }
  };

  // ---------- Fetch Tables ----------
  const fetchTables = async (moduleIdsArray) => {
    try {
      if (!moduleIdsArray || moduleIdsArray.length === 0) {
        setTables([]);
        setRowState({});
        return;
      }

      setLoadingTables(true);
      const res = await axios.post(
        `${BASE_URL}/api/export-to-excel/tables`,
        { moduleIds: moduleIdsArray },
        { headers }
      );

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load tables");
        return;
      }

      const list = res.data.list || [];
      setTables(list);

      // init rowState for tables
      const nextState = {};
      list.forEach((t) => {
        const id = Number(t.fld_id ?? t.id ?? 0);
        if (!id) return;
        nextState[id] = nextState[id] || { selected: false, pdf: false, excel: false };
      });
      setRowState(nextState);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load tables");
    } finally {
      setLoadingTables(false);
    }
  };

  useEffect(() => {
    fetchModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Module selection handlers ----------
  const toggleModule = (moduleId, checked) => {
    setSelectedModuleIds((prev) => {
      const n = new Set(prev);
      if (checked) n.add(Number(moduleId));
      else n.delete(Number(moduleId));
      return n;
    });
  };

  const toggleAllModules = (checked, filteredList) => {
    setSelectedModuleIds((prev) => {
      const n = new Set(prev);
      if (checked) {
        (filteredList || modules).forEach((m) => {
          const id = Number(m.fld_moduleid ?? m.fld_moduleId ?? m.moduleid ?? m.moduleId);
          if (id) n.add(id);
        });
      } else {
        (filteredList || modules).forEach((m) => {
          const id = Number(m.fld_moduleid ?? m.fld_moduleId ?? m.moduleid ?? m.moduleId);
          if (id) n.delete(id);
        });
      }
      return n;
    });
  };

  const handleShowTables = async () => {
    const ids = Array.from(selectedModuleIds);
    if (ids.length === 0) {
      toast.error("Select any one module to show tables.");
      return;
    }
    await fetchTables(ids);
    toast.success("Loaded tables.");
  };

  // ---------- Table row handlers ----------
  const setRow = (tableId, patch) => {
    setRowState((prev) => ({
      ...prev,
      [tableId]: { ...(prev[tableId] || { selected: false, pdf: false, excel: false }), ...patch },
    }));
  };

  const toggleAllTablesSelect = (checked, filteredTableIds) => {
    setRowState((prev) => {
      const next = { ...prev };
      filteredTableIds.forEach((id) => {
        next[id] = next[id] || { selected: false, pdf: false, excel: false };
        next[id].selected = checked;
      });
      return next;
    });
  };

  const validateDates = () => {
    if (!isValidDDMMYYYY(fromDate) || !isValidDDMMYYYY(toDate)) {
      toast.error("Invalid date. Use dd/MM/yyyy.");
      return false;
    }
    return true;
  };

  // ---------- Export ----------
  const handleExport = async () => {
    if (!finYear) {
      toast.error("finYear missing in localStorage. Please login again.");
      return;
    }
    if (!validateDates()) return;

    const selected = Object.entries(rowState)
      .filter(([, v]) => v?.selected)
      .map(([id, v]) => ({ tableId: Number(id), ...v }));

    if (selected.length === 0) {
      toast.error("Select any one table to export.");
      return;
    }

    // ASPX behavior: each selected row exports one file
    try {
      setExporting(true);

      for (const row of selected) {
        const wantsPdf = !!row.pdf;
        const wantsExcel = !!row.excel;

        if (!wantsPdf && !wantsExcel) {
          toast.error("Select any one Pdf or Excel for selected table(s).");
          return;
        }

        if (wantsExcel) {
          await downloadFile({
            url: `${BASE_URL}/api/export-to-excel/export`,
            body: {
              tableId: row.tableId,
              format: "excel",
              fromDate,
              toDate,
            },
            headers,
            fallbackName: `Report_${row.tableId}.xlsx`,
          });
        }

        if (wantsPdf) {
          await downloadFile({
            url: `${BASE_URL}/api/export-to-excel/export`,
            body: {
              tableId: row.tableId,
              format: "pdf",
              fromDate,
              toDate,
            },
            headers,
            fallbackName: `Report_${row.tableId}.pdf`,
          });
        }
      }

      toast.success("Download started. After download, you can click Reset.");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleReset = async () => {
    setSelectedModuleIds(new Set());
    setTables([]);
    setRowState({});
    setModuleSearch("");
    setTableSearch("");
    setFromDate(toDDMMYYYY(new Date(new Date().setMonth(new Date().getMonth() - 1))));
    setToDate(toDDMMYYYY(new Date()));
    toast.info("Reset done.");
  };

  // ---------- Filters ----------
  const filteredModules = useMemo(() => {
    const q = moduleSearch.trim().toLowerCase();
    if (!q) return modules;
    return modules.filter((m) => {
      const name = String(m.fld_modulename ?? m.ModuleName ?? m.modulename ?? "").toLowerCase();
      const id = String(m.fld_moduleid ?? m.moduleid ?? "");
      return name.includes(q) || id.includes(q);
    });
  }, [modules, moduleSearch]);

  const filteredTables = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    if (!q) return tables;
    return tables.filter((t) => {
      const name = String(t.fld_modtablename ?? t.TableName ?? t.name ?? "").toLowerCase();
      const proc = String(t.fld_procedurename ?? t.procedure ?? "").toLowerCase();
      return name.includes(q) || proc.includes(q);
    });
  }, [tables, tableSearch]);

  const filteredTableIds = useMemo(() => {
    return filteredTables
      .map((t) => Number(t.fld_id ?? t.id ?? 0))
      .filter((x) => x > 0);
  }, [filteredTables]);

  return (
    <AdminLayoutWrapper>
      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">Export Data</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header row */}
          <div className="flex justify-between items-center gap-3 flex-wrap mb-6">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={handleBack}
                disabled={exporting}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                } disabled:opacity-60`}
              >
                <ChevronLeft className="w-5 h-5" />
                Back
              </button>

              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Export To Excel / PDF
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {finYear ? `FinYear: ${finYear}` : "FinYear: missing"}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => window.open("/admin/help/export-to-excel", "_blank")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow hover:shadow-md transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
                }`}
              >
                <HelpCircle className="w-5 h-5" />
                Help
              </button>

              <button
                type="button"
                onClick={() => {
                  fetchModules();
                  toast.info("Refreshed");
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow hover:shadow-md transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
                }`}
              >
                <RefreshCw className="w-5 h-5" />
                Refresh
              </button>

              <button
                onClick={() => setShowForm(!showForm)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showForm ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                {showForm ? "Hide Form" : "Show Form"}
              </button>
            </div>
          </div>

          {/* Form */}
          <div className={`rounded-xl border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {showForm && (
              <div className={`${isDark ? "bg-gray-800" : "bg-gray-50"} p-6`}>
                {/* Dates */}
                <div className="grid grid-cols-12 gap-4 items-end max-w-5xl">
                  <div className="col-span-12 md:col-span-6">
                    <label className="text-sm font-medium block mb-1">From date</label>
                    <div className="flex items-center gap-2">
                      <CalendarDays className="w-4 h-4 opacity-60" />
                      <input
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        placeholder="dd/MM/yyyy"
                        className={`w-full px-3 py-2 rounded-lg border ${
                          isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
                        }`}
                      />
                    </div>
                  </div>

                  <div className="col-span-12 md:col-span-6">
                    <label className="text-sm font-medium block mb-1">To date</label>
                    <div className="flex items-center gap-2">
                      <CalendarDays className="w-4 h-4 opacity-60" />
                      <input
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        placeholder="dd/MM/yyyy"
                        className={`w-full px-3 py-2 rounded-lg border ${
                          isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
                        }`}
                      />
                    </div>
                  </div>
                </div>

                {/* Modules Grid */}
                <div className="mt-6">
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                    <div className="text-lg font-semibold">Modules</div>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
                        <input
                          value={moduleSearch}
                          onChange={(e) => setModuleSearch(e.target.value)}
                          placeholder="Search module..."
                          className={`pl-9 pr-3 py-2 rounded-lg border text-sm ${
                            isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
                          }`}
                        />
                      </div>

                      <button
                        type="button"
                        disabled={loadingModules}
                        onClick={() => toggleAllModules(true, filteredModules)}
                        className={`px-3 py-2 rounded-lg text-sm font-semibold border ${
                          isDark ? "bg-gray-700 border-gray-600 hover:bg-gray-600" : "bg-gray-100 border-gray-300 hover:bg-gray-200"
                        }`}
                      >
                        Select All
                      </button>

                      <button
                        type="button"
                        disabled={loadingModules}
                        onClick={() => toggleAllModules(false, filteredModules)}
                        className={`px-3 py-2 rounded-lg text-sm font-semibold border ${
                          isDark ? "bg-gray-700 border-gray-600 hover:bg-gray-600" : "bg-gray-100 border-gray-300 hover:bg-gray-200"
                        }`}
                      >
                        Clear
                      </button>

                      <button
                        type="button"
                        onClick={handleShowTables}
                        disabled={loadingModules || exporting}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow ${buttonClass} ${hoverClass} text-white disabled:opacity-60`}
                      >
                        <Download className="w-5 h-5" />
                        Show Tables
                      </button>
                    </div>
                  </div>

                  <div className={`rounded-xl border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                    <div className="overflow-x-auto">
                      <table className="min-w-[750px] w-full">
                        <thead className={`${buttonClass} text-white`}>
                          <tr>
                            <th className="px-3 py-3 text-center font-semibold border-r border-white/20 w-[90px]">
                              S.No
                            </th>
                            <th className="px-3 py-3 text-left font-semibold border-r border-white/20">
                              Module Name
                            </th>
                            <th className="px-3 py-3 text-center font-semibold w-[120px]">
                              Select
                            </th>
                          </tr>
                        </thead>

                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {loadingModules ? (
                            <tr>
                              <td colSpan={3} className="px-4 py-6 text-center opacity-70">
                                Loading...
                              </td>
                            </tr>
                          ) : filteredModules.length === 0 ? (
                            <tr>
                              <td colSpan={3} className="px-4 py-6 text-center opacity-70">
                                No Records Found
                              </td>
                            </tr>
                          ) : (
                            filteredModules.map((m, idx) => {
                              const id = Number(
                                m.fld_moduleid ??
                                  m.fld_moduleId ??
                                  m.moduleid ??
                                  m.moduleId ??
                                  0
                              );
                              const name = String(m.fld_modulename ?? m.ModuleName ?? m.modulename ?? "");

                              return (
                                <tr
                                  key={id || idx}
                                  className={`transition-colors duration-200 ${
                                    idx % 2 === 0
                                      ? isDark
                                        ? "bg-gray-800"
                                        : "bg-gray-50"
                                      : isDark
                                      ? "bg-gray-900"
                                      : "bg-white"
                                  }`}
                                >
                                  <td className="px-3 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700">
                                    {idx + 1}
                                  </td>
                                  <td className="px-3 py-3 text-sm border-r border-gray-200 dark:border-gray-700">
                                    {name}
                                  </td>
                                  <td className="px-3 py-3 text-center">
                                    <input
                                      type="checkbox"
                                      checked={selectedModuleIds.has(id)}
                                      onChange={(e) => toggleModule(id, e.target.checked)}
                                      className={`w-4 h-4 cursor-pointer ${
                                        isDark ? "accent-blue-400" : "accent-blue-600"
                                      }`}
                                    />
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Tables Grid */}
                <div className="mt-8">
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                    <div className="text-lg font-semibold">Tables</div>

                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
                        <input
                          value={tableSearch}
                          onChange={(e) => setTableSearch(e.target.value)}
                          placeholder="Search table/procedure..."
                          className={`pl-9 pr-3 py-2 rounded-lg border text-sm ${
                            isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
                          }`}
                        />
                      </div>

                      <button
                        type="button"
                        disabled={loadingTables || tables.length === 0}
                        onClick={() => toggleAllTablesSelect(true, filteredTableIds)}
                        className={`px-3 py-2 rounded-lg text-sm font-semibold border ${
                          isDark ? "bg-gray-700 border-gray-600 hover:bg-gray-600" : "bg-gray-100 border-gray-300 hover:bg-gray-200"
                        }`}
                      >
                        Select All
                      </button>

                      <button
                        type="button"
                        disabled={loadingTables || tables.length === 0}
                        onClick={() => toggleAllTablesSelect(false, filteredTableIds)}
                        className={`px-3 py-2 rounded-lg text-sm font-semibold border ${
                          isDark ? "bg-gray-700 border-gray-600 hover:bg-gray-600" : "bg-gray-100 border-gray-300 hover:bg-gray-200"
                        }`}
                      >
                        Clear
                      </button>

                      <button
                        type="button"
                        onClick={handleExport}
                        disabled={exporting || loadingTables || tables.length === 0}
                        className={`flex items-center gap-2 px-5 py-2 rounded-lg font-semibold shadow ${buttonClass} ${hoverClass} text-white disabled:opacity-60`}
                        title="Submit / Export"
                      >
                        <Download className="w-5 h-5" />
                        {exporting ? "Exporting..." : "Export"}
                      </button>

                      <button
                        type="button"
                        onClick={handleReset}
                        className={`px-4 py-2 rounded-lg font-semibold border shadow-sm ${
                          isDark ? "bg-gray-700 border-gray-600 hover:bg-gray-600" : "bg-gray-100 border-gray-300 hover:bg-gray-200"
                        }`}
                      >
                        Reset
                      </button>
                    </div>
                  </div>

                  <div className={`rounded-xl border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                    <div className="overflow-x-auto">
                      <table className="min-w-[1100px] w-full">
                        <thead className={`${buttonClass} text-white`}>
                          <tr>
                            <th className="px-3 py-3 text-center font-semibold border-r border-white/20 w-[90px]">
                              S.No
                            </th>
                            <th className="px-3 py-3 text-left font-semibold border-r border-white/20">
                              Name
                            </th>
                            <th className="px-3 py-3 text-center font-semibold border-r border-white/20 w-[90px]">
                              PDF
                            </th>
                            <th className="px-3 py-3 text-center font-semibold border-r border-white/20 w-[90px]">
                              Excel
                            </th>
                            <th className="px-3 py-3 text-center font-semibold w-[120px]">
                              Select
                            </th>
                          </tr>
                        </thead>

                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {loadingTables ? (
                            <tr>
                              <td colSpan={5} className="px-4 py-6 text-center opacity-70">
                                Loading...
                              </td>
                            </tr>
                          ) : filteredTables.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="px-4 py-6 text-center opacity-70">
                                No Records Found
                              </td>
                            </tr>
                          ) : (
                            filteredTables.map((t, idx) => {
                              const id = Number(t.fld_id ?? t.id ?? 0);
                              const name = String(t.fld_modtablename ?? t.name ?? "");
                              const st = rowState[id] || { selected: false, pdf: false, excel: false };

                              return (
                                <tr
                                  key={id || idx}
                                  className={`transition-colors duration-200 ${
                                    idx % 2 === 0
                                      ? isDark
                                        ? "bg-gray-800"
                                        : "bg-gray-50"
                                      : isDark
                                      ? "bg-gray-900"
                                      : "bg-white"
                                  }`}
                                >
                                  <td className="px-3 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700">
                                    {idx + 1}
                                  </td>

                                  <td className="px-3 py-3 text-sm border-r border-gray-200 dark:border-gray-700">
                                    {name}
                                  </td>

                                  <td className="px-3 py-3 text-center border-r border-gray-200 dark:border-gray-700">
                                    <input
                                      type="checkbox"
                                      checked={!!st.pdf}
                                      onChange={(e) => setRow(id, { pdf: e.target.checked })}
                                      className={`w-4 h-4 cursor-pointer ${
                                        isDark ? "accent-blue-400" : "accent-blue-600"
                                      }`}
                                    />
                                  </td>

                                  <td className="px-3 py-3 text-center border-r border-gray-200 dark:border-gray-700">
                                    <input
                                      type="checkbox"
                                      checked={!!st.excel}
                                      onChange={(e) => setRow(id, { excel: e.target.checked })}
                                      className={`w-4 h-4 cursor-pointer ${
                                        isDark ? "accent-blue-400" : "accent-blue-600"
                                      }`}
                                    />
                                  </td>

                                  <td className="px-3 py-3 text-center">
                                    <input
                                      type="checkbox"
                                      checked={!!st.selected}
                                      onChange={(e) => setRow(id, { selected: e.target.checked })}
                                      className={`w-4 h-4 cursor-pointer ${
                                        isDark ? "accent-blue-400" : "accent-blue-600"
                                      }`}
                                    />
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className={`text-xs mt-3 ${isDark ? "text-white/60" : "text-gray-500"}`}>
                    After download please click Reset button (same as ASPX).
                  </div>
                </div>
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AdminLayoutWrapper>
  );
};

export default AdminExportToExcel;

/* ---------------- Helpers ---------------- */

function toDDMMYYYY(d) {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = String(d.getFullYear());
  return `${dd}/${mm}/${yyyy}`;
}

function isValidDDMMYYYY(s) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(s || "").trim());
  if (!m) return false;
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);
  if (!yyyy || mm < 1 || mm > 12 || dd < 1 || dd > 31) return false;
  return true;
}

async function downloadFile({ url, body, headers, fallbackName }) {
  const res = await axios.post(url, body, {
    headers,
    responseType: "blob",
  });

  const blob = res.data;

  let filename = fallbackName || "Report";
  const cd = res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"];
  if (cd) {
    const match = /filename="?([^"]+)"?/i.exec(cd);
    if (match?.[1]) filename = match[1];
  }

  const link = document.createElement("a");
  const href = window.URL.createObjectURL(blob);
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(href);
}